import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faTriangleExclamation,
  faDatabase,
  faCalendarDays,
  faEarthAmericas,
  faBolt,
  faSignal,
  faCartShopping,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import AuthPromptModal from '../../components/AuthPromptModal';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPrice, pickPrice } from '../../utils/currency';
import { getPlanDetail } from './homeApi';
import './home.css';

const formatData = (mb) => {
  if (mb == null) return '';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const Feature = ({ icon, label, value }) => (
  <div className="feature">
    <FontAwesomeIcon icon={icon} className="feature-icon" />
    <div>
      <span className="feature-label">{label}</span>
      <span className="feature-value">{value}</span>
    </div>
  </div>
);

const PlanDetail = () => {
  const { t } = useTranslation();
  const { bundleId, refillId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currency } = useCurrency();

  const pageTitle = location.state?.title || null;
  const flag = location.state?.flag || null;
  const [state, setState] = useState({ loading: true });
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    getPlanDetail(bundleId, refillId)
      .then((plan) => !cancelled && setState({ plan }))
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [bundleId, refillId]);

  const plan = state.plan;
  const refill = plan?.refill;
  const price = refill ? formatPrice(pickPrice(refill, currency), currency) : null;
  const coverage = plan?.coverage || [];
  const networks = plan?.networks || [];

  const handleBuy = () => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    navigate('/checkout', {
      state: {
        // Backend pricing'i refill.id'yi "product" olarak bekliyor (mobil de
        // createPayPalOrder'a refill.id gönderiyor) — bundle originalId değil.
        productId: String(refillId),
        priceId: refill.priceId,
        plan: {
          countryName: pageTitle || plan.name,
          dataAmount: formatData(refill.amountMb),
          validity: String(refill.amountDays),
          flag,
          priceEuro: refill.priceEuro,
        },
      },
    });
  };

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog plan-detail">
        <button type="button" className="esim-detail-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </button>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{t('home.error.title')}</p>
          </div>
        )}

        {!state.loading && !state.error && plan && refill && (
          <>
            {/* Başlık + satın alma kutusu */}
            <div className="plan-hero">
              <div className="plan-hero-info">
                {(pageTitle || plan.name) && (
                  <p className="plan-eyebrow">{pageTitle || plan.name}</p>
                )}
                <h1 className="plan-title">{refill.title || formatData(refill.amountMb)}</h1>
              </div>

              <div className="plan-buybox">
                <span className="plan-buybox-label">{t('detail.price')}</span>
                {price && <span className="plan-buybox-price">{price}</span>}
                <button type="button" className="plan-buy-lg" onClick={handleBuy}>
                  <FontAwesomeIcon icon={faCartShopping} />
                  {t('detail.buy')}
                </button>
              </div>
            </div>

            {/* Temel özellikler */}
            <h2 className="plan-section-title">{t('detail.basicFeatures')}</h2>
            <div className="plan-features">
              <Feature
                icon={faDatabase}
                label={t('detail.dataAmount')}
                value={formatData(refill.amountMb)}
              />
              <Feature
                icon={faCalendarDays}
                label={t('detail.validityLabel')}
                value={t('detail.validity', { days: refill.amountDays })}
              />
              {coverage.length > 0 && (
                <Feature
                  icon={faEarthAmericas}
                  label={t('detail.coverageArea')}
                  value={coverage.join(', ')}
                />
              )}
            </div>

            {/* Aktivasyon ve ağlar */}
            <h2 className="plan-section-title">{t('detail.activationNetworks')}</h2>
            <div className="plan-features">
              <Feature
                icon={faBolt}
                label={t('detail.activationMethod')}
                value={t('detail.esimActivation')}
              />
            </div>

            {networks.length > 0 && (
              <div className="plan-networks">
                <h3 className="plan-networks-title">
                  <FontAwesomeIcon icon={faSignal} />
                  {t('detail.supportedNetworks')}
                </h3>
                {networks.map((n, i) => (
                  <details key={`${n.title}-${i}`} className="network-row">
                    <summary>
                      {n.flag && <img src={n.flag} alt="" loading="lazy" />}
                      <span>{n.title}</span>
                      <FontAwesomeIcon icon={faChevronDown} className="network-chevron" />
                    </summary>
                    <div className="network-chips">
                      {(n.local_networks || []).map((net) => (
                        <span key={net} className="network-chip">{net}</span>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}

            {/* Açıklama */}
            {plan.description && (
              <>
                <h2 className="plan-section-title">{t('detail.description')}</h2>
                <p className="plan-description">{plan.description}</p>
              </>
            )}
          </>
        )}
      </section>

      <Footer />

      <AuthPromptModal
        open={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
      />
    </div>
  );
};

export default PlanDetail;
